import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Chip,
  Paper,
  CircularProgress,
  Alert,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  CardMedia,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import GradientProgress from '../components/UIKit/GradientProgress';
import {
  ArrowBack,
  Edit,
  Delete,
  School,
  AccessTime,
  PlayArrow,
  KeyboardArrowRight,
  WorkOutline,
  CheckCircle,
  RadioButtonUnchecked,
  Schedule,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { learningPathsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import Card from '../components/UIKit/Card';
import Button from '../components/UIKit/Button';
import ConfirmDialog from '../components/ConfirmDialog';

// Функция для определения цвета сложности (не зависит от состояния)
const getDifficultyColor = (level) => {
  switch (level?.toLowerCase()) {
    case 'начинающий':
    case 'beginner':
      return 'success';
    case 'средний':
    case 'intermediate':
      return 'warning';
    case 'продвинутый':
    case 'advanced':
      return 'error';
    default:
      return 'default';
  }
};

const LearningPathDetail = () => {
  const { t } = useTranslation(['common', 'learningPaths']);
  const { pathId } = useParams();
  const [learningPath, setLearningPath] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isActivePath, setIsActivePath] = useState(false);
  const [isCompletedByUser, setIsCompletedByUser] = useState(false);
  const [isMandatoryForUser, setIsMandatoryForUser] = useState(false);
  const [userProgress, setUserProgress] = useState({
    completedCourses: [],
    nextCourse: null,
    allCompleted: false
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const fetchLearningPath = useCallback(async () => {
    try {
      setLoading(true);
      setError('');

      if (!pathId || pathId === 'undefined' || pathId === 'null') {
        throw new Error(t('learningPaths:error.invalidId'));
      }

      const response = await learningPathsAPI.getById(pathId);

      // Учитываем новую структуру ответа
      let pathData;

      if (response && response.data && response.data.data) {
        // Новая структура с вложенными данными
        pathData = response.data.data;
      } else if (response && response.data) {
        // Старая структура с одним уровнем data
        pathData = response.data;
      } else {
        // Прямой ответ
        pathData = response;
      }

      setLearningPath(pathData);
      setIsCompletedByUser(pathData.isCompletedByUser || false);
      setIsMandatoryForUser(pathData.isMandatoryForUser || false);
      setLoading(false);
    } catch (err) {
      console.error(
        `${t('learningPaths:error.load')} ${pathId}:`,
        err
      );
      setError(t('learningPaths:error.load'));
      setLoading(false);
    }
  }, [pathId, t]);

  // Функция для проверки активной дорожки
  const checkActivePath = useCallback(async () => {
    if (!isAuthenticated || !pathId) return;
    
    try {
      const activeResponse = await learningPathsAPI.getActive();
      const activePathData = activeResponse.data.activeLearningPath;
      
      // Сравниваем ID как строки, так как pathId может быть объектом
      const activePathId = activePathData?.pathId?._id?.toString() || activePathData?.pathId?.toString();
      const currentPathId = pathId?.toString();
      
      if (activePathData && activePathId === currentPathId) {
        setIsActivePath(true);
      } else {
        setIsActivePath(false);
      }
    } catch (err) {
      console.error('Ошибка при проверке активной дорожки:', err);
      setIsActivePath(false);
    }
  }, [isAuthenticated, pathId]);

  // Функция для определения прогресса пользователя (упрощено)
  const calculateUserProgress = useCallback(() => {
    if (!learningPath || !learningPath.courses) return;

    const sortedCourses = learningPath.courses.sort((a, b) => a.order - b.order);
    
    // Используем данные прогресса с бэкенда
    let nextCourse = null;
    let allCompleted = true;
    let completedCount = 0;
    
    for (const courseItem of sortedCourses) {
      if (!courseItem.course) continue;
      
      // Используем данные прогресса из courseProgress
      if (courseItem.courseProgress?.completed) {
        completedCount++;
      } else {
        if (!nextCourse) {
          nextCourse = courseItem.course;
        }
        allCompleted = false;
      }
    }

    setUserProgress({
      completedCourses: [], // Не нужно, так как используем данные с бэкенда
      nextCourse,
      allCompleted,
      completedCount,
      totalCount: sortedCourses.length
    });
  }, [learningPath]);

  // Функция для определения статуса курса (упрощено)
  const getCourseStatus = useCallback((courseId, courseItem) => {
    // Используем данные прогресса из courseProgress
    if (courseItem?.courseProgress?.completed) {
      return 'completed';
    }
    
    // Если это следующий курс
    if (userProgress.nextCourse && userProgress.nextCourse._id.toString() === courseId.toString()) {
      return 'current';
    }
    
    return 'upcoming';
  }, [userProgress]);

  // Функция для получения иконки статуса
  const getStatusIcon = useCallback((status) => {
    switch (status) {
      case 'completed':
        return <CheckCircle sx={{ color: '#4caf50' }} />;
      case 'current':
        return <PlayArrow sx={{ color: '#1976d2' }} />;
      case 'upcoming':
        return <Schedule sx={{ color: '#9e9e9e' }} />;
      default:
        return <RadioButtonUnchecked sx={{ color: '#9e9e9e' }} />;
    }
  }, []);

  // Функция для получения цвета статуса
  const getStatusColor = useCallback((status) => {
    switch (status) {
      case 'completed':
        return '#4caf50';
      case 'current':
        return '#1976d2';
      case 'upcoming':
        return '#9e9e9e';
      default:
        return '#9e9e9e';
    }
  }, []);

  useEffect(() => {
    if (!pathId || pathId === 'undefined' || pathId === 'null') {
      console.error(t('learningPaths:error.invalidId'), pathId);
      setError(t('learningPaths:error.invalidId'));
      setLoading(false);
      return;
    }

    fetchLearningPath();
    checkActivePath();
  }, [pathId, fetchLearningPath, checkActivePath, t]);

  // Вызываем расчет прогресса при изменении данных
  useEffect(() => {
    calculateUserProgress();
  }, [calculateUserProgress]);

  // Показуємо toast коли дорожка автоматично завершена (бекенд вже зафіксував)
  useEffect(() => {
    if (userProgress.allCompleted && isAuthenticated && !isCompletedByUser && learningPath) {
      setIsCompletedByUser(true);
      toast.success(t('learningPaths:pathCompleted'), { toastId: 'path-completed' });
    }
  }, [userProgress.allCompleted, isAuthenticated, isCompletedByUser, learningPath, t]);

  const handleDelete = useCallback(async () => {
    try {
      await learningPathsAPI.delete(pathId);
      toast.success(t('learningPaths:success.deleted'));
      navigate('/learning-paths');
    } catch (err) {
      console.error(t('learningPaths:error.delete'), err);
      toast.error(t('learningPaths:error.delete'));
    }
  }, [pathId, navigate, t]);

  const handleDeleteClick = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    setDeleteDialogOpen(false);
    handleDelete();
  }, [handleDelete]);

  // Функция для установки активной дорожки обучения
  const setActiveLearningPath = useCallback(async () => {
    try {
      await learningPathsAPI.setActive(pathId);
    } catch (err) {
      console.error(t('learningPaths:error.setActive'), err);
    }
  }, [pathId, t]);

  // Функция для получения данных о профессиях
  const getProfessionsList = useCallback((professions) => {
    if (
      !professions ||
      !Array.isArray(professions) ||
      professions.length === 0
    ) {
      return [];
    }

    // Фильтруем null и undefined значения
    return (
      professions
        .filter((prof) => prof !== null && prof !== undefined)
        // Преобразуем объекты профессий для отображения
        .map((prof) => {
          if (typeof prof === 'string') return { id: prof, name: prof };
          if (prof.term_group && prof.term_group.MainTerm && prof.term_group.MainTerm.value)
            return { id: prof.id || prof._id, name: prof.term_group.MainTerm.value };
          return {
            id: prof.id || prof._id,
            name: prof.name || t('learningPaths:unknownProfession'),
          };
        })
    );
  }, [t]);

  // Функция для форматирования времени в часы и минуты
  const formatTimeHoursMinutes = useCallback((minutes) => {
    if (!minutes || minutes <= 0) return null;

    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;

    if (hours > 0 && remainingMinutes > 0) {
      return `${hours} ${t('common:time.shortHour')} ${remainingMinutes} ${t(
        'common:time.shortMin'
      )}`;
    } else if (hours > 0) {
      return `${hours} ${t('common:time.shortHour')}`;
    } else {
      return `${remainingMinutes} ${t('common:time.shortMin')}`;
    }
  }, [t]);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 4 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          component={Link}
          to="/learning-paths"
          sx={{ mt: 2 }}
        >
          {t('learningPaths:detail.backToList')}
        </Button>
      </Container>
    );
  }

  if (!learningPath) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="info" sx={{ mb: 4 }}>
          {t('learningPaths:notFound')}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          component={Link}
          to="/learning-paths"
          sx={{ mt: 2 }}
        >
          {t('learningPaths:detail.backToList')}
        </Button>
      </Container>
    );
  }

  const {
    title,
    description,
    image,
    difficulty,
    categories,
    estimatedTimeMinutes,
    courses,
    createdBy,
    professions,
  } = learningPath;

  // Получаем правильный порядок курсов и фильтруем null значения
  const sortedCourses = courses
    ? [...courses]
        .sort((a, b) => a.order - b.order)
        .filter((course) => course && course.course)
    : [];

  // Получаем список профессий
  const professionsList = getProfessionsList(professions);

  return (
    <Container>
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2}
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Button
          variant="outlined"
          startIcon={<ArrowBack />}
          component={Link}
          to="/learning-paths"
        >
          {t('common:actions.back')}
        </Button>

        {isAuthenticated &&
          (user?.roles?.includes('admin') || 
           (createdBy && user?._id === (createdBy._id || createdBy) && !learningPath.isPublished)) && (
            <Box display="flex" gap={2}>
              <Button
                variant="outlined"
                color="primary"
                component={Link}
                to={`/learning-paths/${pathId}/edit`}
                startIcon={<Edit />}
              >
                {t('common:actions.edit')}
              </Button>
              <Button
                variant="outlined"
                color="error"
                onClick={handleDeleteClick}
                startIcon={<Delete />}
              >
                {t('common:actions.delete')}
              </Button>
            </Box>
          )}
      </Box>

      <Box
        sx={{
          background: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0.6)), url(${
            image || '/images/default-path.jpg'
          })`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          color: 'white',
          padding: '60px 24px',
          borderRadius: '12px',
          marginBottom: '32px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Active Path чип в правом верхнем углу */}
        {isActivePath && (
          <Chip
            label={t('learningPaths:activePath')}
            color="primary"
            variant="filled"
            size="small"
            sx={{
              fontWeight: 'bold',
              position: 'absolute',
              top: 16,
              right: 16,
              zIndex: 1,
            }}
          />
        )}
        {/* Mandatory badge */}
        {isMandatoryForUser && (
          <Chip
            label={t('learningPaths:mandatoryPath')}
            color="warning"
            variant="filled"
            size="small"
            sx={{ fontWeight: 'bold', position: 'absolute', top: 16, left: 16, zIndex: 1 }}
          />
        )}
        {/* Completed badge */}
        {isCompletedByUser && (
          <Chip
            label={t('learningPaths:completedPath')}
            color="success"
            variant="filled"
            size="small"
            sx={{ fontWeight: 'bold', position: 'absolute', top: isMandatoryForUser ? 52 : 16, left: 16, zIndex: 1 }}
          />
        )}
        
        <Box sx={{ maxWidth: isTablet ? '100%' : '70%' }}>
          <Box display="flex" alignItems="center">
            <Chip
              label={t(`common:difficulty.${difficulty}`)}
              color={getDifficultyColor(difficulty)}
              sx={{ mb: 2 }}
            />
          </Box>

          <Typography
            variant="h3"
            component="h1"
            fontWeight="bold"
            sx={{ mb: 2 }}
          >
            {title}
          </Typography>

          <Typography variant="body1" sx={{ mb: 3, fontSize: '1.1rem' }}>
            {description}
          </Typography>

          <Box display="flex" flexWrap="wrap" gap={2} sx={{ mb: 3 }}>
            <Box display="flex" alignItems="center">
              <School sx={{ mr: 1 }} />
              <Typography>
                {courses.length}{' '}
                {t('common:stats.courses')}
              </Typography>
            </Box>

            {estimatedTimeMinutes > 0 && (
              <Box display="flex" alignItems="center">
                <AccessTime sx={{ mr: 1 }} />
                <Typography>
                  {formatTimeHoursMinutes(estimatedTimeMinutes)}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Отображение профессий */}
          {professionsList && professionsList.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
              <Box display="flex" alignItems="center" sx={{ mr: 1 }}>
                <WorkOutline sx={{ mr: 0.5 }} />
                <Typography>
                  {t('learningPaths:professions')}:
                </Typography>
              </Box>
              {professionsList.map((prof) => (
                <Chip
                  key={prof.id}
                  label={prof.name}
                  size="small"
                  color="primary"
                />
              ))}
            </Box>
          )}

          {categories && categories.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
              {categories.map((category, index) => (
                <Chip
                  key={category._id || index}
                  label={category.name}
                  size="small"
                  sx={{
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                  }}
                />
              ))}
            </Box>
          )}

          {/* Прогрес доріжки */}
          {isAuthenticated && courses.length > 0 && (
            <Box sx={{ mb: 3, maxWidth: '500px' }}>
              <Box display="flex" alignItems="center" mb={1} gap={1}>
                <Typography variant="h6" fontWeight="bold">
                  {t('learningPaths:progress')}
                </Typography>
                <Typography variant="body2">
                  ({userProgress.completedCount || 0} {t('learningPaths:of')} {userProgress.totalCount || courses.length} {t('learningPaths:coursesCompleted')})
                </Typography>
              </Box>
              <GradientProgress
                value={((userProgress.completedCount || 0) / (userProgress.totalCount || courses.length)) * 100}
                height={8}
                thresholds={{ low: 30, medium: 70 }}
                sx={{
                  borderRadius: 4,
                }}
              />
              {userProgress.allCompleted && (
                <Typography variant="body2" color="success.main" sx={{ mt: 1, fontWeight: 'bold' }}>
                  🎉 {t('learningPaths:allCoursesCompleted')}
                </Typography>
              )}
            </Box>
          )}

          {isAuthenticated && courses.length > 0 && !userProgress.allCompleted && (
            <Button
              variant="contained"
              color="primary"
              size="large"
              component={Link}
              to={`/courses/${userProgress.nextCourse?.slug || userProgress.nextCourse?._id || sortedCourses[0]?.course?.slug || sortedCourses[0]?.course?._id}`}
              startIcon={<PlayArrow />}
              onClick={setActiveLearningPath}
              sx={{ mt: 1 }}
            >
              {isActivePath ? t('learningPaths:continueLearning') : t('learningPaths:startLearning')}
            </Button>
          )}
        </Box>
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              padding: '24px',
              borderRadius: '12px',
              marginBottom: '24px',
            }}
          >
            {sortedCourses.length === 0 ? (
              <Alert severity="info">
                {t('learningPaths:noCourses')}
              </Alert>
            ) : (
              <Stepper orientation="vertical">
                {sortedCourses.map((courseItem, index) => {
                  // Проверяем, что course существует и не null
                  const course =
                    courseItem && courseItem.course ? courseItem.course : null;
                  if (!course) return null;

                  const courseStatus = getCourseStatus(course._id, courseItem);
                  const statusIcon = getStatusIcon(courseStatus);
                  const statusColor = getStatusColor(courseStatus);

                  return (
                    <Step key={course._id} active completed={courseStatus === 'completed'}>
                      <StepLabel 
                        StepIconComponent={() => (
                          <Box sx={{ color: statusColor }}>
                            {statusIcon}
                          </Box>
                        )}
                      >
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="h6" fontWeight="bold">
                            {index + 1}. {course.title}
                          </Typography>
                          <Chip
                            label={t(`learningPaths:${courseStatus}`)}
                            size="small"
                            sx={{
                              backgroundColor: statusColor,
                              color: 'white',
                              fontWeight: 'bold',
                              fontSize: '0.75rem'
                            }}
                          />
                        </Box>
                      </StepLabel>
                      <StepContent>
                        <Card
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: '12px',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            transition: 'transform 0.3s, box-shadow 0.3s',
                            borderLeft: `4px solid ${statusColor}`,
                            opacity: courseStatus === 'completed' ? 0.8 : 1,
                            minHeight: isTablet ? 100 : 120,
                            flexDirection: isTablet ? 'column' : 'row',
                          }}
                        >
                          {/* Изображение курса - адаптивное отображение */}
                          {course && course.image && (
                            <CardMedia
                              component="img"
                              sx={{ 
                                width: isTablet ? '100%' : 140,
                                height: isTablet ? 120 : 'auto',
                                minWidth: isTablet ? '100%' : 140,
                                flexShrink: 0,
                                objectFit: 'contain'
                              }}
                              image={
                                course.image || '/images/default-course.jpg'
                              }
                              alt={course.title}
                            />
                          )}
                          <Box
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              flexGrow: 1,
                              p: 2,
                              gap: 1.5,
                            }}
                          >
                            <Typography
                              variant="h6"
                              fontWeight="bold"
                            >
                              {index + 1}. {course.title}
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                            >
                              {course.description &&
                              course.description.length > 150
                                ? `${course.description.substring(0, 150)}...`
                                : course.description}
                            </Typography>
                            
                            {/* Прогрес курсу */}
                            {courseItem.courseProgress && (
                              <Box sx={{ mb: 1.5 }}>
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                                  <Typography variant="caption" fontWeight="medium">
                                    {t('learningPaths:progress')}
                                  </Typography>
                                  <Typography variant="caption" fontWeight="bold">
                                    {courseItem.courseProgress.progress || 0}%
                                  </Typography>
                                </Box>
                                <GradientProgress
                                  value={courseItem.courseProgress.progress || 0}
                                  height={6}
                                  thresholds={{ low: 30, medium: 70 }}
                                  sx={{
                                    borderRadius: 3,
                                  }}
                                />
                              </Box>
                            )}
                            
                            {/* Кнопка переходу до курсу */}
                            <Box display="flex" justifyContent={isTablet ? "center" : "flex-end"}>
                              <Button
                                variant="contained"
                                size={isTablet ? "medium" : "small"}
                                color="primary"
                                component={Link}
                                to={`/courses/${course.slug || course._id}`}
                                endIcon={<KeyboardArrowRight />}
                                sx={{ 
                                  minWidth: 'fit-content',
                                  width: isTablet ? '100%' : 'auto'
                                }}
                                onClick={setActiveLearningPath}
                              >
                                {t('learningPaths:goToCourse')}
                              </Button>
                            </Box>
                          </Box>
                        </Card>
                      </StepContent>
                    </Step>
                  );
                })}
              </Stepper>
            )}

            {courses.length > 0 && (
              <Box mt={4} textAlign="center">
                {userProgress.allCompleted ? (
                  <Box>
                    <Typography variant="h6" color="success.main" gutterBottom>
                      🎉 {t('learningPaths:allCoursesCompleted')}
                    </Typography>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    component={Link}
                    to={`/courses/${userProgress.nextCourse?.slug || userProgress.nextCourse?._id || sortedCourses[0]?.course?.slug || sortedCourses[0]?.course?._id}`}
                    startIcon={<PlayArrow />}
                    onClick={setActiveLearningPath}
                  >
                    {isActivePath ? t('learningPaths:continueLearning') : t('learningPaths:startLearning')}
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Діалог підтвердження видалення */}
      <ConfirmDialog
        open={deleteDialogOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title={t('learningPaths:deleteTitle')}
        content={t('learningPaths:deleteConfirmation')}
        confirmText={t('common:actions.delete')}
        cancelText={t('common:actions.cancel')}
      />
    </Container>
  );
};

export default LearningPathDetail;
